import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Lock, Unlock, Fuel, BatteryCharging, Gauge, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { TELEMATICS_MOCK_ENABLED } from "../lib/featureFlags";
import { formatNumber } from "../lib/currencyDisplay";

const MI_TO_KM = 1.60934;

function normalizeTelematics(raw) {
  if (!raw) return null;
  const snap = raw.snapshot || {};
  const odometerKm = raw.odometer_km ?? snap.odometer_km ?? (
    raw.odometer_mi != null ? Math.round(raw.odometer_mi * MI_TO_KM) : (
      snap.odometer_mi != null ? Math.round(snap.odometer_mi * MI_TO_KM) : null
    )
  );
  return {
    lock_state: raw.lock_state ?? "locked",
    fuel_pct: raw.fuel_pct ?? snap.fuel_pct,
    battery_pct: raw.battery_pct ?? snap.battery_pct,
    odometer_km: odometerKm,
    lat: raw.lat ?? snap.lat,
    lng: raw.lng ?? snap.lng,
    last_synced: raw.last_synced ?? snap.updated_at ?? raw.updated_at,
    source: raw.source ?? snap.source,
    provider: raw.provider ?? snap?.provider,
  };
}

function Stat({ icon: Icon, label, value, suffix }) {
  if (value == null || value === "") return null;
  return (
    <div className="zc-card p-4">
      <div className="flex items-center gap-2 text-[var(--zc-text-3)] eyebrow">
        <Icon size={12} /> {label}
      </div>
      <div className="zc-stat text-2xl mt-2">
        {value}<span className="text-base text-[var(--zc-text-3)] ml-1">{suffix}</span>
      </div>
    </div>
  );
}

export default function TelematicsPanel(props) {
  if (!TELEMATICS_MOCK_ENABLED) {
    return null;
  }
  return <TelematicsPanelInner {...props} />;
}

function TelematicsPanelInner({ bookingId, isRenter = true, status = "confirmed", pickupDone = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const pollCount = useRef(0);
  const canControl = isRenter && (status === "confirmed" || status === "active");
  const unlockBlocked = canControl && status === "confirmed" && !pickupDone;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/telematics/booking/${bookingId}`, { withCredentials: true });
      setData(normalizeTelematics(r.data));
      setLoadError(false);
    } catch (e) {
      setLoadError(true);
    }
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) return;
    pollCount.current = 0;
    load();
    const t = setInterval(() => {
      if (pollCount.current >= 30) return;
      pollCount.current += 1;
      load();
    }, 12000);
    return () => clearInterval(t);
  }, [bookingId, load]);

  const setLock = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/telematics/booking/${bookingId}/${action}`, {}, { withCredentials: true });
      setData(normalizeTelematics(r.data));
      toast.success(action === "lock" ? "Vehicle locked" : "Vehicle unlocked");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "complete_pickup_inspection_first") toast.error("Complete pickup inspection first");
      else toast.error(d || "Command failed");
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !data) {
    return (
      <div className="zc-card p-5" data-testid="telematics-panel-error">
        <div className="eyebrow">Trip status</div>
        <div className="text-sm text-[var(--zc-text-2)] mt-2">Telemetry unavailable</div>
        <button onClick={() => { pollCount.current = 0; load(); }} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--zc-accent)]" data-testid="telematics-retry">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="zc-card p-5" data-testid="telematics-panel-loading">
        <div className="eyebrow">Trip status</div>
        <div className="text-sm text-[var(--zc-text-3)] mt-2">Loading vehicle data…</div>
      </div>
    );
  }

  const locked = data.lock_state === "locked";
  const hasLocation = data.lat != null && data.lng != null;
  const showTouchlessUnlock = TELEMATICS_MOCK_ENABLED;

  return (
    <div className="zc-card p-5" data-testid="telematics-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow">Trip status</div>
          {showTouchlessUnlock ? (
            <div className="zc-title text-lg mt-0.5">
              {locked ? "Vehicle is locked" : "Vehicle is unlocked"}
            </div>
          ) : (
            <div className="text-sm text-[var(--zc-text-2)] mt-0.5">Vehicle telemetry</div>
          )}
          {data.source && (
            <div className="text-2xs text-[var(--zc-text-3)] mt-0.5 capitalize">
              Updated from {data.source.replace(/_/g, " ")}
            </div>
          )}
        </div>
        <button onClick={load} className="text-[var(--zc-text-3)] hover:text-[var(--zc-text)]" title="Refresh" data-testid="telematics-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {showTouchlessUnlock && unlockBlocked && (
        <div className="mt-3 text-xs text-[var(--zc-warn,#f59e0b)] p-3 rounded-lg bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.25)]">
          Complete the pickup condition inspection above to unlock the vehicle.
        </div>
      )}
      {showTouchlessUnlock && canControl && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            onClick={() => setLock("unlock")}
            disabled={busy || !locked || unlockBlocked}
            className={"h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition " +
              (locked ? "zc-btn-primary" : "bg-[var(--zc-bg-3)] text-[var(--zc-text-3)] cursor-not-allowed")}
            data-testid="telematics-unlock-btn"
          >
            <Unlock size={14} /> Unlock
          </button>
          <button
            onClick={() => setLock("lock")}
            disabled={busy || locked}
            className={"h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition " +
              (!locked ? "zc-btn-primary" : "bg-[var(--zc-bg-3)] text-[var(--zc-text-3)] cursor-not-allowed")}
            data-testid="telematics-lock-btn"
          >
            <Lock size={14} /> Lock
          </button>
        </div>
      )}
      {!showTouchlessUnlock && canControl && (
        <div className="zc-hint mt-3">
          Touchless unlock is not available yet — use your host&apos;s handoff instructions.
        </div>
      )}
      {showTouchlessUnlock && !canControl && (
        <div className="zc-hint mt-3">
          Lock/unlock activates after pickup. {isRenter ? "" : "Owner view is read-only."}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
        <Stat icon={Fuel} label="Fuel" value={data.fuel_pct} suffix="%" />
        <Stat icon={BatteryCharging} label="Battery" value={data.battery_pct} suffix="%" />
        <Stat
          icon={Gauge}
          label="Odometer"
          value={data.odometer_km != null ? Number(data.odometer_km).toLocaleString() : null}
          suffix="km"
        />
      </div>

      {hasLocation && (
        <div className="mt-4 zc-hint flex items-center gap-1.5">
          <MapPin size={11} /> {formatNumber(data.lat, 4)}, {formatNumber(data.lng, 4)}
          {data.last_synced && <> · synced {new Date(data.last_synced).toLocaleTimeString()}</>}
        </div>
      )}
      {!hasLocation && data.last_synced && (
        <div className="mt-4 zc-hint">
          Last updated {new Date(data.last_synced).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
